import type { FC } from 'react';
import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@apollo/react-hooks';

import { ErrorDisplay } from '@confluence/error-boundary';
import { useFeatureDiscovery } from '@confluence/feature-discovery';

import { GuestBannerSpacePermissionsPortalQuery } from './GuestBannerSpacePermissionsPortal.graphql';
import { getExternalCollaboratorEntitlementFromQuery } from './Queries/getExternalCollaboratorEntitlementFromQuery';
import type { GuestBannerSpacePermissionsPortalQuery as GuestBannerSpacePermissionsPortalQueryType } from './__types__/GuestBannerSpacePermissionsPortalQuery';
import { GuestBannerSpacePermissions } from './GuestBannerSpacePermissions';

export const GUEST_BANNER_SPACE_PERMS_PORTAL_ID =
	'external-collab-guest-banner-space-perms-portal-container';

export const GuestBannerSpacePermissionsPortal: FC<{}> = () => {
	// Check ExCo entitlement data
	const { data: entitlementData, error: queryError } =
		useQuery<GuestBannerSpacePermissionsPortalQueryType>(GuestBannerSpacePermissionsPortalQuery);

	const [showFeature, hideFeatureNextTime, featureDiscoveryError] = useFeatureDiscovery(
		GUEST_BANNER_SPACE_PERMS_PORTAL_ID,
	);

	const showBannerRef = useRef<boolean>(false);
	const showBanner = showFeature && getExternalCollaboratorEntitlementFromQuery(entitlementData);
	if (showBanner) {
		showBannerRef.current = true;
	}

	const error = queryError || featureDiscoveryError;
	if (error) {
		return <ErrorDisplay error={error} />;
	}

	// If entitled and FFs are enabled and the portal target exists and there are no query errors, then render the banner
	const portalTarget = showBannerRef.current
		? document.getElementById(GUEST_BANNER_SPACE_PERMS_PORTAL_ID)
		: null;

	if (portalTarget) {
		return ReactDOM.createPortal(
			<GuestBannerSpacePermissions onDismiss={hideFeatureNextTime} />,
			portalTarget,
		);
	}
	return null;
};
